import { useState } from "react";
import "./business-card.css";
import {
  CARD_H,
  CARD_W,
  FIXED_ADDRESS,
  FIXED_COMPANY,
  MIN_BYTES,
  TARGET_DPI,
  generateCardPdf,
  isSportradar,
  safeCardFileName,
  type CardData,
} from "./generate-card";

// No one's personal name/phone is baked in as a default - every visitor
// of this shared page should start from a blank, generic form.
const DEFAULTS: CardData = {
  name: "",
  title: "Data Collector",
  company: FIXED_COMPANY,
  phone: "",
  address: FIXED_ADDRESS,
  email: "",
};

function CompanyMark({ company }: { company: string }) {
  if (isSportradar(company)) {
    return (
      <div className="bcp-company-mark bcp-wordmark">
        <span className="bcp-wm-light">sp</span>
        <span className="bcp-wm-o">
          <span className="bcp-wm-o-dot" />
        </span>
        <span className="bcp-wm-light">rt</span>
        <span className="bcp-wm-bold">radar</span>
      </div>
    );
  }
  return <div className="bcp-company-mark">{company}</div>;
}

export function BusinessCardPage() {
  const [data, setData] = useState<CardData>(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ text: string; error: boolean }>({ text: "", error: false });

  function setField<K extends keyof CardData>(key: K, value: string) {
    setData((prev) => ({ ...prev, [key]: value }));
  }

  async function handleDownload() {
    if (!data.name.trim()) {
      setStatus({ text: "Wpisz imię i nazwisko przed pobraniem.", error: true });
      return;
    }
    setBusy(true);
    setStatus({ text: "Przygotowuję plik…", error: false });
    try {
      const payload: CardData = {
        name: data.name.trim(),
        title: data.title.trim(),
        company: data.company.trim() || "Company",
        phone: data.phone.trim(),
        address: data.address.trim(),
        email: data.email.trim(),
      };
      const blob = await generateCardPdf(payload);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `business-card-${safeCardFileName(payload.name)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      const kb = Math.max(1, Math.round(blob.size / 1024));
      setStatus({
        text: blob.size < MIN_BYTES
          ? `Zapisano (${kb} KB). Uwaga: to poniżej zwykłego minimum 10 KB — sprawdź czy plik jest czytelny.`
          : `Zapisano (${kb} KB).`,
        error: false,
      });
    } catch (exc) {
      setStatus({ text: exc instanceof Error ? exc.message : "Nie udało się zapisać pliku. Spróbuj ponownie.", error: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bcp-page-root">
      <div className="bcp-page">
        <header className="bcp-intro">
          <span className="bcp-eyebrow">e-Business Visa · India</span>
          <h1>Wizytówka do wniosku wizowego</h1>
          <p>
            Wypełnij dane po lewej — podgląd wizytówki aktualizuje się na bieżąco. Gdy wygląda dobrze, pobierz plik PDF
            (format 85&nbsp;×&nbsp;55&nbsp;mm, 10–300&nbsp;KB) i dołącz go do wniosku. Treść wizytówki musi być po
            angielsku.
          </p>
        </header>

        <div className="bcp-workspace">
          <div className="bcp-panel bcp-form">
            <h2>Dane na wizytówce</h2>
            <p className="bcp-hint">
              Formularz zawsze startuje pusty — nikt nie zostawia tu swoich danych dla kolejnej osoby. Firma i adres są
              takie same dla całego zespołu i nie podlegają edycji.
            </p>

            <div className="bcp-field">
              <label htmlFor="f-name">Imię i nazwisko</label>
              <input id="f-name" type="text" value={data.name} maxLength={60} placeholder="Jan Kowalski" onChange={(e) => setField("name", e.target.value)} />
            </div>
            <div className="bcp-field">
              <label htmlFor="f-title">Stanowisko</label>
              <input id="f-title" type="text" value={data.title} maxLength={60} onChange={(e) => setField("title", e.target.value)} />
            </div>
            <div className="bcp-field">
              <label htmlFor="f-company">Firma</label>
              <input id="f-company" type="text" value={data.company} readOnly disabled />
            </div>
            <div className="bcp-field">
              <label htmlFor="f-phone">Telefon</label>
              <input id="f-phone" type="text" value={data.phone} maxLength={40} placeholder="+48 000 000 000" onChange={(e) => setField("phone", e.target.value)} />
            </div>
            <div className="bcp-field">
              <label htmlFor="f-address">Adres</label>
              <textarea id="f-address" value={data.address} readOnly disabled />
            </div>
            <div className="bcp-field">
              <label htmlFor="f-email">Email</label>
              <input id="f-email" type="text" value={data.email} maxLength={60} placeholder="name@sportradar.com" onChange={(e) => setField("email", e.target.value)} />
            </div>
          </div>

          <div className="bcp-panel bcp-preview">
            <div className="bcp-stage">
              <div className="bcp-card">
                <div className="bcp-content">
                  <div className="bcp-who">
                    <div className="bcp-name">{data.name || "Full Name"}</div>
                    <div className="bcp-company">{data.company || "Company"}</div>
                    <div className="bcp-title">{data.title}</div>
                  </div>
                  <div className="bcp-spacer" />
                  <div className="bcp-divider" />
                  <div className="bcp-spacer" />
                  <div className="bcp-contact">
                    <div><strong>T</strong> <span>{data.phone || "—"}</span></div>
                    <div><strong>E</strong> <span>{data.email || "—"}</span></div>
                    <div>{data.address}</div>
                  </div>
                </div>
                <CompanyMark company={data.company || "Company"} />
              </div>
            </div>

            <div className="bcp-actions">
              <button className="bcp-btn-primary" type="button" onClick={handleDownload} disabled={busy}>
                ↓ Pobierz jako PDF
              </button>
            </div>
            <p className="bcp-spec">
              PDF · 85 × 55 mm · obraz {CARD_W} × {CARD_H} px ({TARGET_DPI}&nbsp;dpi) · 10–300 KB
            </p>
            <p className={`bcp-status${status.error ? " bcp-error" : ""}`}>{status.text}</p>
          </div>
        </div>

        <footer className="bcp-footer">
          Skan wizytówki jest jednym z formalnych wymogów przy składaniu wniosku o indyjską e-Business Visa — potwierdza
          status zawodowy wnioskodawcy i reprezentowaną firmę. Dane wpisane tutaj powinny pokrywać się z informacjami
          podanymi we wniosku wizowym.
        </footer>
      </div>
    </div>
  );
}
